import { MainApiService, xVerifyService } from '_services';
import { FIELDS_KEYS } from '_constants/forms';
import { sleep } from '_helpers/common';

import {
  checkIfFileAllowed,
  getFileExtension,
  getFileExtensionsString,
} from '_helpers/file';

const llcLegalNameRegExp = new RegExp(
  /(?!.*(?:[ ][lL][cC]$))(?!.*(?:[ ][pP][lL][lL][cC]$))(?!.*(?:[ ][pP][aA]$))(?!.*(?:[ ][cC][oO][rR][pP]$))(?!.*(?:[ ][iI][nN][cC]$))(?!.*(?:[ ][iI][nN][cC][oO][rR][pP][oO][rR][aA][tT][eE][dD]$))^[ a-zA-Z0-9&-]+$/
);

export const validateTradeName = (options) => async (value) => {
  let validationResult = true;

  const { setProcessing, setError, checkCorporationAvailability, values } =
    options || {};

  const notCountedCharacters = ['&', '-', ' '];

  const notCountedWords = ['and', 'or', 'of', 'the', 'a'];

  const notAllowedEndings = [
    'corp',
    'corporation',
    'inc',
    'incorporated',
    'pa',
  ];

  if (values[FIELDS_KEYS.organizedState] === 'TN') {
    notAllowedEndings.push('usa');
  }

  if (values[FIELDS_KEYS.organizedState] === 'IN') {
    notAllowedEndings.push('coop');
    notAllowedEndings.push('co-op');
    notAllowedEndings.push('cooperative');
    notAllowedEndings.push('coops');
    notAllowedEndings.push('co-ops');
    notAllowedEndings.push('cooperatives');
  }

  if (values[FIELDS_KEYS.organizedState] === 'NJ') {
    notAllowedEndings.push('ltd');
  }

  if (value.trim().length === 0) {
    return 'Business Name should not be empty';
  }

  if (value.trim().toLowerCase() === 'llc') {
    validationResult = 'Business Name should not be LLC';
  }

  if (
    value
      .trim()
      .toLowerCase()
      .replace(/\s{2,}/g, ' ')
      .split(' ')
      .some((fragment) => notAllowedEndings.includes(fragment))
  ) {
    if (values[FIELDS_KEYS.organizedState] === 'TN') {
      return 'The legal name may not contain any of the following endings: Corp, Corporation, Inc, Incorporated, PA, USA';
    }

    if (values[FIELDS_KEYS.organizedState] === 'IN') {
      return 'The legal name may not contain any of the following endings: Corp, Corporation, Inc, Incorporated, PA, Coop, Co-op, Coops, Co-ops, Cooperative, Cooperatives';
    }

    if (values[FIELDS_KEYS.organizedState] === 'NJ') {
      return 'The legal name may not contain any of the following endings: Corp, Corporation, Inc, Incorporated, PA, Ltd';
    }

    return 'The legal name may not contain any of the following endings: Corp, Corporation, Inc, Incorporated, PA';
  }

  if (
    values[FIELDS_KEYS.organizedState] === 'NJ' &&
    value
      .trim()
      .toLowerCase()
      .replace(/\s{2,}/g, ' ')
      .split(' ')
      .filter((fragment) => !notCountedWords.includes(fragment))
      .join('')
      .split('')
      .filter((character) => !notCountedCharacters.includes(character)).length <
      4
  ) {
    return 'Business Name must be at least 4 characters, excluding leading articles, spaces and conjunctions (AND, OR, OF, &)';
  }

  if (checkCorporationAvailability === false) {
    return validationResult;
  }

  if (setProcessing) {
    setProcessing(true);
  }

  if (setError) {
    setError(null);
  }

  try {
    const { data } = await MainApiService.checkCorporationAvailability({
      name: value,
      state: values[FIELDS_KEYS.organizedState],
    });

    if (!data) {
      validationResult = 'Limited Liability Company Name Not Available';
    }

    await sleep(2000);

    if (setProcessing) {
      setProcessing(false);
    }
  } catch (e) {
    const clientMessage = e?.message || e?.toString() || 'Something went wrong';

    await sleep(2000);

    if (clientMessage.includes('Network Error')) {
      setError &&
        setError({
          message: `${clientMessage}. Please check your internet connection`,
        });
    } else {
      setError({ message: clientMessage });
    }

    if (setProcessing) {
      setProcessing(false);
    }

    return false;
  }

  return validationResult;
};

export const validateEmail = (options) => async (value) => {
  let validationResult = true;
  const { setProcessing, useEmailVerificationService } = options || {};

  if (useEmailVerificationService === false) {
    return validationResult;
  }

  if (setProcessing) {
    setProcessing(true);
  }

  let emailValue = value;

  if (emailValue.indexOf('+') > -1 && emailValue.indexOf('gmail.com') > -1) {
    emailValue =
      emailValue.substring(0, emailValue.indexOf('+')) +
      emailValue.substring(emailValue.indexOf('@'), emailValue.length);
  }

  const { status, reason } = await xVerifyService.verifyEmail(emailValue);

  if (xVerifyService.INVALID_EMAIL_STATUSES.includes(status)) {
    const defaultMessage = 'Email address is not valid';
    // eslint-disable-next-line max-len, prettier/prettier
    validationResult =
      xVerifyService.VALIDATION_MESSAGE[reason] || defaultMessage;
  }

  if (setProcessing) {
    setProcessing(false);
  }

  return validationResult;
};

export const validateYear = () => (value) => {
  const currentDate = new Date();

  if (currentDate.getFullYear() - value >= 25) {
    return 'The year cannot be more than 1 year in the future or more than 25 years in the past.';
  }

  return true;
};

export const validateAddress = () => (value) => {
  if (!value) {
    return true;
  }

  if (
    !/(?!.*(?:[pP][oO] [bB][oO][xX]))^[ a-zA-Z0-9\\/-]+$/.test(value) ||
    !/(?!.*(?:[pP][oO][bB][oO][xX]))^[ a-zA-Z0-9\\/-]+$/.test(value) ||
    !/(?!.*(?:[pP][oO][sS][tT] [oO][fF][fF][iI][cC][eE] [bB][oO][xX]))^[ a-zA-Z0-9\\/-]+$/.test(
      value
    ) ||
    /\bP(ost|ostal)?([ \.]*(O|0)(ffice)?)?([ \.]*Box)\b/i.test(value)
  ) {
    return 'PO Boxes are not acceptable';
  }

  return true;
};

export const validateCardNumber = (options) => (value) => {
  const { erroredFields } = options || {};
  const isTestCard = value === '0000000000000000';

  return !isTestCard && erroredFields.cardNumber
    ? erroredFields.cardNumber
    : true;
};

export const validateCVC = (options) => () => {
  const { erroredFields } = options || {};
  return erroredFields.cvc || true;
};

export const validateCardMonth = (options) => (value) => {
  const { values, clearErrors, checkIfCardExpired } = options || {};

  const isCardExpired = checkIfCardExpired({
    cardMonth: value,
    cardYear: values[FIELDS_KEYS.cardYear],
  });

  if (!isCardExpired) {
    clearErrors && clearErrors(FIELDS_KEYS.cardYear);
  }

  return isCardExpired && values[FIELDS_KEYS.cardYear]
    ? 'This card expired'
    : true;
};

export const validateCardYear = (options) => (value) => {
  const { values, clearErrors, checkIfCardExpired } = options || {};

  const isCardExpired = checkIfCardExpired({
    cardMonth: values[FIELDS_KEYS.cardMonth],
    cardYear: value,
  });

  if (!isCardExpired) {
    clearErrors && clearErrors(FIELDS_KEYS.cardMonth);
  }

  return isCardExpired && values[FIELDS_KEYS.cardMonth]
    ? 'This card expired'
    : true;
};

export const validatePhone = () => (value) => {
  const pureNumber = value?.replace(/\D/g, '');

  if (pureNumber?.length !== 10) {
    return 'Phone number should be equal 10 symbols';
  }

  return true;
};

export const validateSSN = (options) => (value) => {
  const { noValidate } = options || {};

  if (noValidate) {
    return true;
  }

  const pureNumber = value?.replace(/\D/g, '');

  if (pureNumber?.length !== 9) {
    return 'Phone number should be equal 9 symbols';
  }

  return true;
};

export const validateLLCLegalName = () => (value) => {
  if (
    !llcLegalNameRegExp.test(value.trim()) ||
    ['corp', 'inc', 'pa'].includes(value.trim().toLowerCase())
  ) {
    return 'The legal name may not contain any of the following endings: Corp, Inc, PA';
  }
  return true;
};

export const validateAltCompanyName = (options) => (value) => {
  const { values } = options || {};

  if (value.trim().length === 0) {
    return 'Alternate Business Name should not be empty';
  }

  if (value.trim().toLowerCase() === 'llc') {
    return 'Alternate Business Name should not be LLC';
  }

  if (
    value.trim().toLowerCase() ===
      values[FIELDS_KEYS.tradeName].trim().toLowerCase() ||
    value.trim().toLowerCase() ===
      values[FIELDS_KEYS.tradeName]
        .toLowerCase()
        .substring(
          0,
          values[FIELDS_KEYS.tradeName].toLowerCase().indexOf('llc')
        )
        .trim() ||
    (value
      .toLowerCase()
      .substring(0, value.toLowerCase().indexOf('llc'))
      .trim() === values[FIELDS_KEYS.tradeName].toLowerCase().trim() &&
      value
        .toLowerCase()
        .substring(0, value.toLowerCase().indexOf('llc'))
        .trim() !== '')
  ) {
    return 'Your alternate name cannot be the same as your primary LLC name';
  }

  return true;
};

export const validateProductDescription = () => (value) => {
  const cleanValue = value
    ?.replace(/[\r\n]+/gm, ' ')
    .replace(/\s{2,}/g, ' ')
    .trim();

  if (!cleanValue) {
    return 'Please provide Product Description';
  }

  if (cleanValue?.length > 50) {
    return 'Product Description should be less or equal 50 symbols';
  }

  return true;
};

export const validateFile = (options) => (value) => {
  const { fileTypes, fileType, noValidate } = options || {};

  if (noValidate) {
    return true;
  }

  const { allowedExtensions } =
    fileTypes?.find(({ code }) => code === fileType) || {};

  const fileExtension = getFileExtension(value?.name);

  const isExtensionAllowed = checkIfFileAllowed(
    fileExtension,
    allowedExtensions
  );

  if (!isExtensionAllowed) {
    return `Incorrect file type. Allowed ${getFileExtensionsString(
      allowedExtensions
    )}`;
  }

  const fileSizeMb = value.size / 1024 / 1024;

  if (fileSizeMb >= 3.8) {
    return 'File size exceeds 3.8Mb';
  }

  return true;
};

export const validateDriverLicenceOrID = () => (value) => {
  if (value?.trim()?.length === 0) {
    return "Please provide Driver's License or ID Number";
  }

  return true;
};

export const validateFullName = () => (value) => {
  if (!/^(?!\s*$)[ a-zA-Z\-]+$/i.test(value.trim())) {
    return 'Full Name should contain only letters and - symbols';
  }

  return true;
};
